import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function PhotoTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;
  const contact = [personal.email, personal.phone, personal.location, personal.website, personal.linkedin].filter(Boolean);

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="flex items-center gap-6 pb-6 mb-6 border-b border-slate-200" style={{ flexDirection: s.headerAlignment === 'right' ? 'row-reverse' : 'row', textAlign: s.headerAlignment === 'center' ? 'center' : 'left' }}>
        {s.photo ? (
          <img src={s.photo} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-slate-200 shrink-0" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-slate-200 shrink-0" />
        )}
        <div>
          <h1 className="font-bold text-slate-900 tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${2.25 * hScale}rem` }}>
            {personal.fullName || 'Your Name'}
          </h1>
          {contact.length > 0 && <p className="text-slate-500 mt-2" style={{ fontSize: `${0.875 * bScale}rem` }}>{contact.join(' • ')}</p>}
        </div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wide text-slate-900 mb-3 pb-1 border-b-2 border-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>Work Experience</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <article key={exp.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{exp.title}</h3>
                    <span className="text-slate-500" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700 font-medium" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company}{exp.location && `, ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </article>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wide text-slate-900 mb-3 pb-1 border-b-2 border-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <article key={edu.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{edu.school}</h3>
                    <span className="text-slate-500" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wide text-slate-900 mb-3 pb-1 border-b-2 border-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>Skills</h2>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-white" style={{ fontSize: `${0.75 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wide text-slate-900 mb-3 pb-1 border-b-2 border-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>Projects</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <article key={proj.id}>
                  <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{proj.name}</h3>
                  <p className="text-slate-600" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && <p className="text-slate-500 mt-1" style={{ fontSize: `${0.75 * bScale}rem` }}>{proj.technologies.join(', ')}</p>}
                </article>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wide text-slate-900 mb-3 pb-1 border-b-2 border-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>Certifications</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {certifications.map((cert) => (
                <div key={cert.id} className="flex justify-between items-baseline">
                  <span className="font-medium text-slate-800" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer}{cert.date && ` • ${cert.date}`}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
